import { z } from 'zod';
import publishedCatalog from './native-releases.json' with { type: 'json' };
export const nativeDownloadsBase = 'https://downloads.atum.tech/atum-remote';
export const nativeRepository = 'https://github.com/atumtech/atum-Remote';
// Conservative macOS bundle limits also fit Windows installers; npm/Cargo/Tauri stay identical.
export const nativeVersionSchema = z
    .string()
    .regex(/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/)
    .refine((version) => version.split('.').every((part, index) => Number(part) <= (index === 0 ? 9999 : 99)), 'Installer versions must fit MAJOR <= 9999, MINOR <= 99 and PATCH <= 99');
export const nativeTargetSchema = z.enum(['windows-x86_64', 'darwin-universal']);
export function installerName(version, target) {
    return `ATUM-Remote_${version}_${target}${target === 'windows-x86_64' ? '-setup.exe' : '.dmg'}`;
}
export function updaterName(version, target) {
    return target === 'windows-x86_64'
        ? `ATUM-Remote_${version}_${target}.zip`
        : `ATUM-Remote_${version}_${target}.app.tar.gz`;
}
export const updaterArtifactSchema = z.strictObject({
    filename: z.string(),
    size: z.number().int().positive().max(2_000_000_000),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    signature: z
        .string()
        .min(100)
        .max(2048)
        .regex(/^[A-Za-z0-9+/]+={0,2}$/),
});
export const nativeArtifactSchema = z.strictObject({
    target: nativeTargetSchema,
    filename: z.string(),
    size: z.number().int().positive().max(2_000_000_000),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    signing: z.enum(['updater-signature', 'developer-id-notarized']),
    updater: updaterArtifactSchema,
});
export const nativeReleaseSchema = z
    .strictObject({
    schemaVersion: z.literal(1),
    product: z.literal('atum-remote'),
    layout: z.literal('single-app-v1'),
    version: nativeVersionSchema,
    sourceCommit: z.string().regex(/^[a-f0-9]{40}$/),
    publishedAt: z.iso.datetime(),
    notes: z.string().trim().min(1).max(20_000),
    artifacts: z.array(nativeArtifactSchema).min(1).max(2),
})
    .superRefine((release, context) => {
    const targets = new Set();
    for (const artifact of release.artifacts) {
        if (targets.has(artifact.target) ||
            artifact.filename !== installerName(release.version, artifact.target) ||
            artifact.updater.filename !== updaterName(release.version, artifact.target) ||
            artifact.signing !==
                (artifact.target === 'windows-x86_64' ? 'updater-signature' : 'developer-id-notarized')) {
            context.addIssue({
                code: 'custom',
                message: 'Duplicate target or mismatched installer metadata',
            });
        }
        targets.add(artifact.target);
    }
});
export const nativeCatalogSchema = z
    .strictObject({
    schemaVersion: z.literal(1),
    updaterPublicKey: z
        .string()
        .min(40)
        .max(1024)
        .regex(/^[A-Za-z0-9+/]+={0,2}$/)
        .nullable(),
    releases: z.array(nativeReleaseSchema).max(100),
})
    .refine((catalog) => new Set(catalog.releases.map((release) => release.version)).size === catalog.releases.length, 'Release versions must be unique')
    .refine((catalog) => catalog.releases.length === 0 || catalog.updaterPublicKey !== null, 'Published releases require a pinned updater public key');
export const nativeReleaseCatalog = nativeCatalogSchema.parse(publishedCatalog);
export function findNativeUpdate(target, currentVersion, releases = nativeReleaseCatalog.releases) {
    return [...releases]
        .filter((release) => compareNativeVersions(release.version, currentVersion) > 0 &&
        release.artifacts.some((artifact) => artifact.target === target))
        .sort((a, b) => compareNativeVersions(b.version, a.version))[0];
}
export function nativeDownloadUrl(release, artifact) {
    return `${nativeDownloadsBase}/v${release.version}/${artifact.filename}`;
}
export function compareNativeVersions(left, right) {
    const a = left.split('.').map(Number);
    const b = right.split('.').map(Number);
    for (let index = 0; index < 3; index++) {
        const difference = (a[index] ?? 0) - (b[index] ?? 0);
        if (difference)
            return difference;
    }
    return 0;
}
//# sourceMappingURL=native-releases.js.map